import React, { useCallback, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Fade,
    Alert
} from '@mui/material';
import {
    CloudUpload,
    AudioFile,
    Delete,
    CheckCircle
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const FileDropZone = ({ onFileSelect, selectedFile, accept = "audio/*, .m4a, .mp3, .wav, .aac, .ogg", error }) => {
    const theme = useTheme();
    const [isDragOver, setIsDragOver] = useState(false);
    const [fileError, setFileError] = useState('');

    const validateFile = useCallback((file) => {
        // Check file type
        const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/mp4', 'audio/x-m4a'];
        const allowedExtensions = ['.mp3', '.wav', '.ogg', '.aac', '.m4a'];
        
        const isValidType = allowedTypes.some(type => file.type.includes(type.split('/')[1])) ||
                           allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        
        if (!isValidType) {
            return 'Please select a valid audio file (MP3, WAV, OGG, AAC, M4A)';
        }

        // Check file size (limit to 500MB)
        const maxSize = 500 * 1024 * 1024; // 500MB in bytes
        if (file.size > maxSize) {
            return 'File size must be less than 500MB';
        }

        return null;
    }, []);

    const handleFileSelection = useCallback((file) => {
        const validationError = validateFile(file);
        if (validationError) {
            setFileError(validationError);
            return;
        }
        
        setFileError('');
        onFileSelect(file);
    }, [validateFile, onFileSelect]);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleFileSelection(files[0]);
        }
    }, [handleFileSelection]);

    const handleFileInputChange = useCallback((e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            handleFileSelection(files[0]);
        }
    }, [handleFileSelection]);

    const handleRemoveFile = useCallback(() => {
        onFileSelect(null);
        setFileError('');
    }, [onFileSelect]);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const inputId = React.useId();

    return (
        <Box sx={{ width: '100%' }}>
            {/* Drop Zone */}
            <Paper
                elevation={isDragOver ? 6 : 1}
                sx={{
                    border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
                    borderRadius: 3,
                    py: 3,
                    px: 2,
                    textAlign: 'center',
                    backgroundColor: isDragOver 
                        ? theme.palette.action.hover 
                        : theme.palette.background.paper,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[4],
                    }
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById(inputId)?.click()}
            >
                <CloudUpload 
                    sx={{ 
                        fontSize: 40, 
                        color: isDragOver ? theme.palette.primary.main : theme.palette.text.secondary,
                        mb: 1,
                        transition: 'color 0.3s ease-in-out'
                    }} 
                />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                    {isDragOver ? 'Drop file here' : 'Drop or click to upload'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                    opacity: 0.8,
                    lineHeight: 1.3
                }}>
                    MP3, WAV, OGG, AAC, M4A • Max 500MB
                </Typography>
                
                <input
                    id={inputId}
                    type="file"
                    accept={accept}
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                />
            </Paper>

            {/* Error Display */}
            {(fileError || error) && (
                <Fade in={true}>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {fileError || error}
                    </Alert>
                </Fade>
            )}

            {/* Selected File Display */}
            {selectedFile && (
                <Fade in={true}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            mt: 2, 
                            border: `1px solid ${theme.palette.success.main}`,
                            borderRadius: 2,
                            backgroundColor: theme.palette.success.light + '08'
                        }}
                    >
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <AudioFile 
                                sx={{ 
                                    color: theme.palette.success.main,
                                    fontSize: 20
                                }} 
                            />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontWeight: 500,
                                            color: theme.palette.text.primary,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {selectedFile.name}
                                    </Typography>
                                    <CheckCircle 
                                        sx={{ 
                                            color: theme.palette.success.main,
                                            fontSize: 16
                                        }} 
                                    />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(selectedFile.size)} • {selectedFile.type || 'Audio file'}
                                </Typography>
                            </Box>
                            <IconButton 
                                onClick={handleRemoveFile}
                                size="small"
                                sx={{
                                    color: theme.palette.error.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.error.main + '10'
                                    }
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    </Paper>
                </Fade>
            )}
        </Box>
    );
};

export default FileDropZone; 